import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateFollowUpVisit,
  useFollowUpVisitList,
  useFollowUpVisitTransitions,
  FOLLOW_UP_VISIT_STATUS_LABELS,
  FOLLOW_UP_VISIT_KIND_LABELS,
  FOLLOW_UP_VISIT_CHANNEL_LABELS,
  type FollowUpVisitChannel,
  type FollowUpVisitKind,
  type FollowUpVisitStatus,
  type FollowUpVisitSummaryDto,
} from '@/hooks/useFollowUp';
import {
  FOLLOW_UP_PERMISSIONS,
  canSeeFollowUpPlansMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

const STATUS_OPTIONS: FollowUpVisitStatus[] = [
  'scheduled',
  'in_progress',
  'completed',
  'missed',
  'cancelled',
];
const KIND_OPTIONS: FollowUpVisitKind[] = [
  'general',
  'post_infusion',
  'adverse_event',
  'satisfaction',
  'other',
];
const CHANNEL_OPTIONS: FollowUpVisitChannel[] = [
  'phone',
  'onsite',
  'wechat',
  'video',
  'other',
];

export default function FollowUpVisitsPage() {
  const { permissions } = useAuth();
  const { rows, total, page, limit, loading, error, fetchVisits } =
    useFollowUpVisitList();
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState<FollowUpVisitStatus | ''>('');
  const [pageInput, setPageInput] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const [actionTarget, setActionTarget] = useState<FollowUpVisitSummaryDto | null>(
    null,
  );
  const [actionKey, setActionKey] = useState(0);

  const canRead = canSeeFollowUpPlansMenu(permissions);
  const canCreate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.CREATE);
  const canUpdate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.UPDATE);

  useEffect(() => {
    if (!canRead) return;
    void fetchVisits({
      planId: filterPlan || undefined,
      status: filterStatus || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [filterPlan, filterStatus, pageInput, canRead, fetchVisits]);

  if (!canRead) return <AccessDenied resource="随访节点" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">随访节点</h1>
          <p className="text-sm text-muted-foreground">
            随访计划下的具体节点：电话 / 现场 / 视频 / 微信。
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setCreateKey((k) => k + 1);
              setCreateOpen(true);
            }}
          >
            新建节点
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="filter-plan">计划 ID</Label>
            <Input
              id="filter-plan"
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              placeholder="follow_up_plan id"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-status">状态</Label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus((e.target.value || '') as FollowUpVisitStatus | '')
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">全部</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {FOLLOW_UP_VISIT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">计划</th>
                <th className="px-3 py-2">类型</th>
                <th className="px-3 py-2">渠道</th>
                <th className="px-3 py-2">状态</th>
                <th className="px-3 py-2">排定时间</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    加载中…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    暂无节点
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2">{row.sequenceNo}</td>
                  <td className="px-3 py-2">{row.planId}</td>
                  <td className="px-3 py-2">{FOLLOW_UP_VISIT_KIND_LABELS[row.visitKind]}</td>
                  <td className="px-3 py-2">
                    {FOLLOW_UP_VISIT_CHANNEL_LABELS[row.channel]}
                  </td>
                  <td className="px-3 py-2">
                    {FOLLOW_UP_VISIT_STATUS_LABELS[row.status]}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(row.scheduledAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    {canUpdate && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setActionKey((k) => k + 1);
                          setActionTarget(row);
                        }}
                      >
                        管理
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          共 {total} 条 · 第 {page} / {totalPages} 页
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput <= 1}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput >= totalPages}
            onClick={() => setPageInput((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      </div>

      {createOpen && (
        <Card className="space-y-3 border-blue-200 bg-blue-50 p-4">
          <h2 className="text-base font-semibold">新建随访节点</h2>
          <CreateVisitForm
            key={createKey}
            onCreated={() => {
              setCreateOpen(false);
              setPageInput(1);
              void fetchVisits({ page: 1, limit: 20 });
            }}
            onCancel={() => setCreateOpen(false)}
          />
        </Card>
      )}

      {actionTarget && (
        <VisitActionPanel
          key={actionKey}
          visit={actionTarget}
          onDone={() => {
            setActionTarget(null);
            void fetchVisits({
              planId: filterPlan || undefined,
              status: filterStatus || undefined,
              page: pageInput,
              limit: 20,
            });
          }}
          onCancel={() => setActionTarget(null)}
        />
      )}
    </div>
  );
}

function CreateVisitForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { createVisit, loading, error } = useCreateFollowUpVisit();
  const [planId, setPlanId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [visitKind, setVisitKind] = useState<FollowUpVisitKind>('general');
  const [channel, setChannel] = useState<FollowUpVisitChannel>('phone');
  const [notes, setNotes] = useState('');

  const submit = async () => {
    if (!planId.trim() || !scheduledAt) return;
    const result = await createVisit({
      planId: planId.trim(),
      scheduledAt: new Date(scheduledAt).toISOString(),
      visitKind,
      channel,
      notes: notes.trim() || undefined,
    });
    if (result) onCreated();
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="visit-plan">计划 ID</Label>
          <Input
            id="visit-plan"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="visit-scheduled">排定时间</Label>
          <Input
            id="visit-scheduled"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="visit-kind">类型</Label>
          <select
            id="visit-kind"
            value={visitKind}
            onChange={(e) => setVisitKind(e.target.value as FollowUpVisitKind)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {FOLLOW_UP_VISIT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="visit-channel">渠道</Label>
          <select
            id="visit-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as FollowUpVisitChannel)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {FOLLOW_UP_VISIT_CHANNEL_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="visit-notes">备注</Label>
        <textarea
          id="visit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          rows={3}
          className="w-full rounded-md border bg-background p-2 text-sm"
        />
      </div>
      {error && <div className="text-sm text-red-700">{error.message}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          取消
        </Button>
        <Button onClick={submit} disabled={loading}>
          {loading ? '提交中…' : '创建'}
        </Button>
      </div>
    </div>
  );
}

function VisitActionPanel({
  visit,
  onDone,
  onCancel,
}: {
  visit: FollowUpVisitSummaryDto;
  onDone: () => void;
  onCancel: () => void;
}) {
  const transitions = useFollowUpVisitTransitions();
  const [summary, setSummary] = useState('');
  const [reason, setReason] = useState('');

  const run = async (
    action: 'start' | 'complete' | 'missed' | 'cancel',
  ) => {
    if (action === 'start') {
      const r = await transitions.start(visit.id, {
        expectedVersion: visit.version,
      });
      if (r) onDone();
    } else if (action === 'complete') {
      const r = await transitions.complete(visit.id, {
        expectedVersion: visit.version,
        summary: summary.trim(),
      });
      if (r) onDone();
    } else if (action === 'missed') {
      const r = await transitions.markMissed(visit.id, {
        expectedVersion: visit.version,
        reason: reason.trim(),
      });
      if (r) onDone();
    } else if (action === 'cancel') {
      const r = await transitions.cancel(visit.id, {
        expectedVersion: visit.version,
        reason: reason.trim(),
      });
      if (r) onDone();
    }
  };

  return (
    <Card className="space-y-3 border-amber-300 bg-amber-50 p-4">
      <h3 className="text-base font-semibold">
        节点 #{visit.sequenceNo} · 状态 {FOLLOW_UP_VISIT_STATUS_LABELS[visit.status]}
      </h3>
      {visit.status === 'scheduled' && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run('start')} disabled={transitions.loading}>
            开始随访
          </Button>
          <Button variant="outline" onClick={() => run('missed')} disabled={!reason.trim() || transitions.loading}>
            标记未应答
          </Button>
          <Button variant="outline" onClick={() => run('cancel')} disabled={!reason.trim() || transitions.loading}>
            取消
          </Button>
        </div>
      )}
      {visit.status === 'in_progress' && (
        <div className="space-y-2">
          <Label htmlFor="visit-summary">总结（必填）</Label>
          <textarea
            id="visit-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full rounded-md border bg-background p-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => run('complete')}
              disabled={!summary.trim() || transitions.loading}
            >
              完成节点
            </Button>
            <Button variant="outline" onClick={() => run('cancel')} disabled={!reason.trim() || transitions.loading}>
              取消
            </Button>
          </div>
        </div>
      )}
      {(visit.status === 'scheduled' || visit.status === 'in_progress') && (
        <div className="space-y-1">
          <Label htmlFor="visit-reason">未应答 / 取消原因</Label>
          <Input
            id="visit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}
      {(visit.status === 'completed' ||
        visit.status === 'missed' ||
        visit.status === 'cancelled') && (
        <div className="text-sm text-muted-foreground">
          该节点已是终态，无可执行操作。
        </div>
      )}
      {transitions.error && (
        <div className="text-sm text-red-700">{transitions.error.message}</div>
      )}
      <div className="flex justify-end">
        <Button variant="ghost" onClick={onCancel}>
          收起
        </Button>
      </div>
    </Card>
  );
}
